import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getAttachmentsForExtraction } from './aiDataExtractionRequest'
import {
  pickEnergyCertificateFileFromList,
  pickFloorPlanFileFromList,
  revokeDocumentPreviewUrl,
} from './aiUploadedAttachmentsPure'
import { hasSelectedAttachments } from './uploadedAttachmentGuards'
import { registerInFlightUpload } from './inFlightUploadRegistry'
import { useAiDocumentAttachment } from './useAiDocumentAttachment'
import { useAiImageAttachments } from './useAiImageAttachments'
import { useSyncRef } from './useSyncRef'

export const useAiUploadedAttachments = () => {
  const { t } = useTranslation()
  const [documentsMaxFileSizeErrorText, setDocumentsMaxFileSizeErrorText] = useState<string | undefined>()
  const pendingUploadPromisesRef = useRef<Record<string, Promise<void>>>({})

  const registerFloorPlanInFlightUpload = useCallback(
    (uploadPromise: Promise<void>) => registerInFlightUpload(pendingUploadPromisesRef, 'floor-plan', uploadPromise),
    []
  )

  const registerEnergyCertificateInFlightUpload = useCallback(
    (uploadPromise: Promise<void>) =>
      registerInFlightUpload(pendingUploadPromisesRef, 'energy-certificate', uploadPromise),
    []
  )

  const oversizeDocumentErrorMessage = t('wizard.uploadAiBanner.modal.documents.maxFileSizeError')
  const unsupportedFileTypeErrorMessage = t('wizard.uploadAiBanner.modal.documents.unsupportedFileTypeError')

  const imageAttachments = useAiImageAttachments({ pendingUploadPromisesRef })

  const floorPlan = useAiDocumentAttachment({
    oversizeErrorMessage: oversizeDocumentErrorMessage,
    pickEligibleFile: pickFloorPlanFileFromList,
    registerInFlightUpload: registerFloorPlanInFlightUpload,
    setDocumentsMaxFileSizeErrorText,
    unsupportedFileTypeErrorMessage,
    isFloorPlanApiUpload: true,
  })

  const energyCertificate = useAiDocumentAttachment({
    oversizeErrorMessage: oversizeDocumentErrorMessage,
    pickEligibleFile: pickEnergyCertificateFileFromList,
    registerInFlightUpload: registerEnergyCertificateInFlightUpload,
    setDocumentsMaxFileSizeErrorText,
    unsupportedFileTypeErrorMessage,
  })

  const uploadedFloorPlanAttachmentRef = useSyncRef(floorPlan.uploadedAttachment)
  const uploadedEnergyCertificateAttachmentRef = useSyncRef(energyCertificate.uploadedAttachment)
  const floorPlanDocumentCleanupRef = useRef(floorPlan.document)
  const energyCertificateDocumentCleanupRef = useRef(energyCertificate.document)

  useEffect(() => {
    floorPlanDocumentCleanupRef.current = floorPlan.document
  }, [floorPlan.document])

  useEffect(() => {
    energyCertificateDocumentCleanupRef.current = energyCertificate.document
  }, [energyCertificate.document])

  useEffect(() => {
    return () => {
      revokeDocumentPreviewUrl(floorPlanDocumentCleanupRef.current)
      revokeDocumentPreviewUrl(energyCertificateDocumentCleanupRef.current)
    }
  }, [])

  const hasSelectedFiles = hasSelectedAttachments(
    imageAttachments.images,
    floorPlan.document,
    energyCertificate.document
  )

  const waitForPendingUploads = async () => {
    await Promise.allSettled(Object.values(pendingUploadPromisesRef.current))
  }

  const getLatestAttachmentsForExtraction = () =>
    getAttachmentsForExtraction(
      imageAttachments.imagesRef.current,
      imageAttachments.uploadedImageAttachmentsRef.current,
      uploadedFloorPlanAttachmentRef.current,
      uploadedEnergyCertificateAttachmentRef.current
    )

  return {
    documentsMaxFileSizeErrorText,
    energyCertificateDocument: energyCertificate.document,
    energyCertificateErrorText: energyCertificate.errorText,
    floorPlanDocument: floorPlan.document,
    floorPlanErrorText: floorPlan.errorText,
    getLatestAttachmentsForExtraction,
    hasSelectedFiles,
    imageErrorText: imageAttachments.imageErrorText,
    images: imageAttachments.images,
    onEnergyCertificateRemove: energyCertificate.onRemove,
    onEnergyCertificateSelect: energyCertificate.onSelect,
    onFloorPlanRemove: floorPlan.onRemove,
    onFloorPlanSelect: floorPlan.onSelect,
    onImageRemove: imageAttachments.onImageRemove,
    onImageReorder: imageAttachments.onImageReorder,
    onImageSelect: imageAttachments.onImageSelect,
    waitForPendingUploads,
  }
}
